import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import React from "react";

/**
 * A small worked example with made-up numbers, shown under a calculated table so
 * anyone can see exactly how a figure is arrived at — the method written in words
 * next to it, made concrete here in numbers.
 */
interface Props {
  title: string;
  intro: React.ReactNode;
  columns: string[];
  rows: React.ReactNode[][];
  conclusion: React.ReactNode;
}

const WorkedExample: React.FC<Props> = ({ title, intro, columns, rows, conclusion }) => (
  <Box
    sx={{
      mt: 2,
      border: "1px dashed",
      borderColor: "divider",
      borderRadius: 2,
      p: 2,
      backgroundColor: "action.hover",
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
      {intro}
    </Typography>
    <Box sx={{ overflowX: "auto" }}>
      <Table
        size="small"
        sx={{ width: "auto", "& td, & th": { border: 0, py: 0.35, pr: 3, pl: 0 } }}
      >
        <TableHead>
          <TableRow>
            {columns.map((c, i) => (
              <TableCell
                key={c}
                align={i === 0 ? "left" : "right"}
                sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}
              >
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={String(r[0])}>
              {r.map((cell, ci) => (
                <TableCell
                  key={columns[ci]}
                  align={ci === 0 ? "left" : "right"}
                  sx={{ fontVariantNumeric: "tabular-nums", fontWeight: ci === 0 ? 600 : 400 }}
                >
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
    <Typography
      variant="body2"
      color="textSecondary"
      sx={{ mt: 1.25, "& b": { color: "text.primary" } }}
    >
      {conclusion}
    </Typography>
  </Box>
);

export default WorkedExample;
